<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i :class="`mdi mdi-${isEdit ? 'pencil' : 'plus-circle'} me-2`"></i>
            {{ isEdit ? 'Edit' : 'Add New' }} Internet Point
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <div class="row">
              <!-- Basic Information -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Location Name <span class="text-danger">*</span></label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="formData.name"
                      placeholder="e.g., City Center WiFi Hotspot"
                      required
                  >
                </div>

                <div class="mb-3">
                  <label class="form-label">Region <span class="text-danger">*</span></label>
                  <input
                      type="text"
                      class="form-control"
                      v-model="formData.region"
                      placeholder="e.g., Downtown, Eastlands"
                      list="regions-list"
                      required
                  >
                  <datalist id="regions-list">
                    <option v-for="region in existingRegions" :key="region" :value="region">
                    </option>
                  </datalist>
                </div>

                <div class="mb-3">
                  <label class="form-label">Service Provider <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.provider" required @change="updateProviderInfo">
                    <option value="">Select service provider</option>
                    <option v-for="provider in kenyanProviders" :key="provider.id" :value="provider.name">
                      {{ provider.name }}
                    </option>
                  </select>
                  <div v-if="selectedProvider" class="mt-2 p-2 bg-light rounded">
                    <small class="text-muted">
                      <i class="mdi mdi-information"></i>
                      {{ selectedProvider.description }}
                    </small>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Connection Type <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.connectionType" required>
                    <option value="">Select connection type</option>
                    <option v-for="type in availableConnectionTypes" :key="type" :value="type">
                      {{ type }}
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Speed (Mbps) <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input
                        type="number"
                        class="form-control"
                        v-model="formData.speed"
                        placeholder="e.g., 100"
                        min="1"
                        required
                    >
                    <span class="input-group-text">Mbps</span>
                  </div>
                  <div v-if="selectedProvider && selectedProvider.speedRanges" class="mt-2">
                    <small class="text-muted">
                      Typical {{ formData.provider }} speeds: {{ getProviderSpeedRange() }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Location Information -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Location Coordinates <span class="text-danger">*</span></label>
                  <div class="alert alert-info py-2">
                    <small>Click on the map to set location or enter coordinates manually</small>
                  </div>

                  <div class="row g-2">
                    <div class="col-6">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text">Lat</span>
                        <input
                            type="number"
                            class="form-control"
                            v-model="formData.latitude"
                            step="0.000001"
                            required
                        >
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="input-group input-group-sm">
                        <span class="input-group-text">Lng</span>
                        <input
                            type="number"
                            class="form-control"
                            v-model="formData.longitude"
                            step="0.000001"
                            required
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Mini Map -->
                <div class="mb-3">
                  <div id="mini-map" style="height: 200px; border: 1px solid #ddd; border-radius: 4px;"></div>
                </div>
              </div>

              <!-- Provider-Specific Information -->
              <div class="col-12">
                <div class="card bg-light border-0">
                  <div class="card-body">
                    <h6 class="card-title">
                      <i class="mdi mdi-domain me-1"></i>
                      Provider Information
                      <span v-if="formData.provider" class="badge bg-primary ms-2">{{ formData.provider }}</span>
                    </h6>
                    <div class="row">
                      <div class="col-md-3">
                        <div class="mb-2">
                          <label class="form-label small">Monthly Cost (KES)</label>
                          <input
                              type="number"
                              class="form-control form-control-sm"
                              v-model="formData.cost"
                              :placeholder="getProviderCostPlaceholder()"
                          >
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-2">
                          <label class="form-label small">Reliability (%)</label>
                          <input
                              type="number"
                              class="form-control form-control-sm"
                              v-model="formData.reliability"
                              placeholder="e.g., 95"
                              min="0"
                              max="100"
                          >
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-2">
                          <label class="form-label small">Customer Rating</label>
                          <select class="form-select form-select-sm" v-model="formData.rating">
                            <option value="">Select rating</option>
                            <option value="5">⭐⭐⭐⭐⭐ (5/5)</option>
                            <option value="4">⭐⭐⭐⭐ (4/5)</option>
                            <option value="3">⭐⭐⭐ (3/5)</option>
                            <option value="2">⭐⭐ (2/5)</option>
                            <option value="1">⭐ (1/5)</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-md-3">
                        <div class="mb-2">
                          <label class="form-label small">Package Name</label>
                          <input
                              type="text"
                              class="form-control form-control-sm"
                              v-model="formData.packageName"
                              :placeholder="getPackagePlaceholder()"
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Provider Coverage Info -->
                    <div v-if="selectedProvider" class="mt-3 p-2 border rounded">
                      <small class="text-muted">
                        <strong>Coverage:</strong> {{ selectedProvider.coverage }} |
                        <strong>Technology:</strong> {{ selectedProvider.technology.join(', ') }} |
                        <strong>Market Share:</strong> {{ selectedProvider.marketShare }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Information -->
              <div class="col-12 mt-3">
                <div class="mb-3">
                  <label class="form-label">Notes/Description</label>
                  <textarea
                      class="form-control"
                      v-model="formData.notes"
                      rows="3"
                      placeholder="Additional information about this internet point, installation details, contact info, etc..."
                  ></textarea>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i> Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="handleSubmit" :disabled="!isFormValid">
            <i :class="`mdi mdi-${isEdit ? 'check' : 'plus'} me-1`"></i>
            {{ isEdit ? 'Update' : 'Add' }} Internet Point
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
export default {
  name: 'AddEditInternetPointModal',
  props: {
    point: { type: Object, default: null },
    show: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false }
  },
  data() {
    return {
      formData: {
        name: '', region: '', latitude: '', longitude: '', connectionType: '',
        speed: '', notes: '', provider: '', cost: '', reliability: '',
        rating: '', packageName: ''
      },
      miniMap: null,
      marker: null,
      existingRegions: ['Downtown', 'Eastlands', 'Westlands', 'Kileleshwa', 'Karen', 'Runda', 'Kasarani', 'Embakasi'],
      kenyanProviders: [
        {
          id: 1, name: 'Safaricom',
          description: 'Kenya\'s largest telecom provider with extensive fiber and mobile network coverage',
          coverage: 'Nationwide', marketShare: '65%',
          technology: ['Fiber', '4G', '5G'],
          speedRanges: { fiber: '10-1000 Mbps', '4G': '5-100 Mbps', '5G': '100-1000 Mbps' },
          packages: ['Safaricom Home Fiber', 'Bonga Data', 'Unlimited Data']
        },
        {
          id: 2, name: 'Zuku (Wananchi Group)',
          description: 'Leading cable TV and fiber internet provider in urban areas',
          coverage: 'Major Cities', marketShare: '15%',
          technology: ['Fiber', 'Cable'],
          speedRanges: { fiber: '10-500 Mbps', cable: '5-100 Mbps' },
          packages: ['Zuku Fiber', 'Zuku Triple Play', 'Zuku Internet']
        },
        {
          id: 3, name: 'Airtel Kenya',
          description: 'International telecom with strong 4G coverage and competitive data packages',
          coverage: 'Urban & Rural', marketShare: '20%',
          technology: ['4G', 'Fiber'],
          speedRanges: { '4G': '5-150 Mbps', fiber: '10-200 Mbps' },
          packages: ['Airtel Money Data', 'Unlimited Bundles', 'Airtel Fiber']
        },
        {
          id: 4, name: 'Telkom Kenya',
          description: 'Government-owned telecom providing nationwide coverage with competitive rates',
          coverage: 'Nationwide', marketShare: '8%',
          technology: ['4G', 'Fiber'],
          speedRanges: { '4G': '5-100 Mbps', fiber: '10-300 Mbps' },
          packages: ['Telkom Data', 'Faiba Fiber', 'Telkom Postpay']
        },
        {
          id: 5, name: 'JTL Faiba',
          description: 'Fiber-focused provider with high-speed internet in select urban areas',
          coverage: 'Select Urban Areas', marketShare: '3%',
          technology: ['Fiber', '4G'],
          speedRanges: { fiber: '20-1000 Mbps', '4G': '10-200 Mbps' },
          packages: ['Faiba Fiber Home', 'Faiba 4G', 'Faiba Business']
        },
        {
          id: 6, name: 'Poa Internet',
          description: 'Community-focused WiFi provider serving low-income areas',
          coverage: 'Urban Communities', marketShare: '2%',
          technology: ['Wireless', 'Fiber'],
          speedRanges: { wireless: '5-50 Mbps', fiber: '10-100 Mbps' },
          packages: ['Poa Daily', 'Poa Weekly', 'Poa Monthly']
        },
        {
          id: 7, name: 'Liquid Telecom',
          description: 'Business-focused provider with enterprise-grade connectivity solutions',
          coverage: 'Business Districts', marketShare: '4%',
          technology: ['Fiber', 'Satellite'],
          speedRanges: { fiber: '50-10000 Mbps', satellite: '10-500 Mbps' },
          packages: ['Liquid Enterprise', 'Liquid Fiber', 'Liquid Cloud']
        },
        {
          id: 8, name: 'Mawingu Networks',
          description: 'Wireless internet provider focusing on underserved rural and urban areas',
          coverage: 'Rural & Urban', marketShare: '1%',
          technology: ['Wireless', '4G'],
          speedRanges: { wireless: '5-100 Mbps', '4G': '5-50 Mbps' },
          packages: ['Mawingu Home', 'Mawingu Business', 'Mawingu Mobile']
        }
      ]
    }
  },
  computed: {
    isFormValid() {
      return this.formData.name && this.formData.region && this.formData.latitude &&
          this.formData.longitude && this.formData.connectionType &&
          this.formData.speed && this.formData.provider;
    },
    selectedProvider() {
      return this.kenyanProviders.find(p => p.name === this.formData.provider);
    },
    availableConnectionTypes() {
      if (this.selectedProvider) {
        return this.selectedProvider.technology;
      }
      return ['Fiber', '4G', '5G', 'Wireless', 'Satellite'];
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initMiniMap();
          if (this.isEdit && this.point) {
            this.loadPointData();
          }
        });
      }
    }
  },
  methods: {
    loadPointData() {
      if (this.point) {
        this.formData = { ...this.formData, ...this.point };
        this.updateMarkerPosition();
      }
    },
    updateProviderInfo() {
      // Clear connection type when provider changes to show only available types
      this.formData.connectionType = '';
    },
    getProviderSpeedRange() {
      if (this.selectedProvider && this.formData.connectionType) {
        return this.selectedProvider.speedRanges[this.formData.connectionType] || '';
      }
      return '';
    },
    getProviderCostPlaceholder() {
      const costs = {
        'Safaricom': 'e.g., 3000-15000',
        'Zuku (Wananchi Group)': 'e.g., 2500-12000',
        'Airtel Kenya': 'e.g., 2000-10000',
        'Telkom Kenya': 'e.g., 1500-8000',
        'JTL Faiba': 'e.g., 3500-20000',
        'Poa Internet': 'e.g., 500-2000',
        'Liquid Telecom': 'e.g., 5000-50000',
        'Mawingu Networks': 'e.g., 1000-5000'
      };
      return costs[this.formData.provider] || 'e.g., 2000';
    },
    getPackagePlaceholder() {
      if (this.selectedProvider && this.selectedProvider.packages) {
        return this.selectedProvider.packages[0] || 'Package name';
      }
      return 'Package name';
    },
    initMiniMap() {
      const lat = this.formData.latitude || -1.286389;
      const lng = this.formData.longitude || 36.817223;

      this.miniMap = new google.maps.Map(document.getElementById('mini-map'), {
        zoom: 13,
        center: { lat: parseFloat(lat), lng: parseFloat(lng) },
        mapTypeId: 'roadmap'
      });

      this.marker = new google.maps.Marker({
        position: { lat: parseFloat(lat), lng: parseFloat(lng) },
        map: this.miniMap,
        draggable: true
      });

      this.marker.addListener('dragend', (event) => {
        this.formData.latitude = event.latLng.lat().toFixed(6);
        this.formData.longitude = event.latLng.lng().toFixed(6);
      });

      this.miniMap.addListener('click', (event) => {
        this.formData.latitude = event.latLng.lat().toFixed(6);
        this.formData.longitude = event.latLng.lng().toFixed(6);
        this.updateMarkerPosition();
      });
    },
    updateMarkerPosition() {
      if (this.marker && this.formData.latitude && this.formData.longitude) {
        const position = new google.maps.LatLng(
            parseFloat(this.formData.latitude),
            parseFloat(this.formData.longitude)
        );
        this.marker.setPosition(position);
        this.miniMap.setCenter(position);
      }
    },
    handleSubmit() {
      if (!this.isFormValid) return;
      const data = { ...this.formData, id: this.isEdit ? this.point.id : undefined };
      this.$emit('save', data);
    },
    resetForm() {
      this.formData = {
        name: '', region: '', latitude: '', longitude: '', connectionType: '',
        speed: '', notes: '', provider: '', cost: '', reliability: '',
        rating: '', packageName: ''
      };
    }
  }
}
</script>

<style scoped>
.modal.show { display: block; }
.form-label { font-weight: 600; color: #495057; }
.input-group-text { background-color: #f8f9fa; }
#mini-map { border-radius: 0.25rem; }
</style>